import { Subscribable } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import SimpleTopicsManager from 'managers/discussion/simpleTopics';
import events from 'managers/events';
import { IWCCStorageManager } from 'managers/iStorage';
import { withEffects } from 'mixins/withEffects';
import { Person } from 'models/person';
import { SimpleTopic } from 'models/simpleTopic';

export interface ActivityNavigationManagerConfig {
    taskId: Subscribable<string | undefined>
    topicId: Subscribable<string | undefined>
    navigate: Action<[string]>
    storage: IWCCStorageManager
}

export default class ActivityNavigationManager {
    private effects = withEffects();

    private autoNavigationAllowed = ko.observable(false);

    private topicId: Subscribable<string | undefined>

    private user: Subscribable<Person | undefined>
    private topics: Subscribable<Array<SimpleTopic>>
    private currentTopic: Subscribable<SimpleTopic | undefined>
    private nextTopic: Subscribable<SimpleTopic | undefined>

    private isUpdatingTopics: Subscribable<boolean>

    isReady: Subscribable<boolean>

    private navigateFunc: Action<[string]>

    constructor({ taskId, topicId, navigate, storage }: ActivityNavigationManagerConfig) {
        const effects = this.effects;

        this.topicId = topicId;
        this.navigateFunc = navigate;

        const userManager = storage.get(CurrentUserManager, { discussionId: taskId });
        this.user = userManager.pluck(m => m.person);

        const topicsManager = storage.get(SimpleTopicsManager, { discussionId: taskId });
        const loadingTopics = topicsManager.pluck(m => m.loading, true).extend({ deferred: true });
        this.isUpdatingTopics = topicsManager.pluck(m => m.updating, false);
        this.topics = topicsManager.pluck(m => m.topics, []);

        this.currentTopic = this.topics.find(t => t.id() == this.topicId());
        this.nextTopic = ko.pureComputed(() => this.getNextTopic());        

        this.isReady = ko.pureComputed(() => this.user() != undefined && !loadingTopics());

        effects.register(topic => {
            if (topic != undefined)
                return topic.isAnswered.subscribe(this.onTopicIsAnsweredStateChange.bind(this));
        }, [this.currentTopic]);

        effects.register([
            events.topicChangeRequested.on(this.onTopicChangeRequested.bind(this)),

            ko.computed(() => this.showNextTopicIfNeeded()).extend({ deferred: true }),
            ko.computed(() => this.showNextTopicIfInvalid()).extend({ deferred: true })
        ]);
    }

    activateTopic(topicId: string) {
        this.autoNavigationAllowed(false);
        this.navigateFunc(topicId);
    }

    dispose() {
        this.effects.dispose();
    }

    private getNextTopic() {
        const topics = this.topics();

        const idx = topics.findIndex(topic => topic.id() == this.topicId());

        if (idx !== -1)
            return topics[idx + 1];
    }

    private showNextTopicIfNeeded() {
        if (this.isReady()) {
            const user = this.user();
            const currentTopic = this.currentTopic();
            const nextTopic = this.nextTopic();

            const navigationAllowed = user != undefined &&
                currentTopic != undefined &&
                currentTopic.isInformationOnlyTopic() &&
                user.isRegular() &&
                this.autoNavigationAllowed() &&
                !this.isUpdatingTopics();

            if (navigationAllowed && nextTopic != undefined) {
                const nextTopicId = nextTopic.id();

                if (nextTopicId != undefined)
                    ko.ignoreDependencies(() => this.navigateFunc(nextTopicId));
            }
        }
    }

    private showNextTopicIfInvalid() {
        if (this.isReady()) {
            const navigationAllowed = !this.isUpdatingTopics() && this.topics().every(topic => topic.id() !== this.topicId());

            if (navigationAllowed) {
                const nextTopic = this.topics().find(topic => topic.shouldBeAnswered()) ?? this.topics()[0];

                if (nextTopic !== undefined) {
                    const nextTopicId = nextTopic.id();

                    if (nextTopicId != undefined)
                        ko.ignoreDependencies(() => this.navigateFunc(nextTopicId));
                }
            }
        }
    }

    private onTopicIsAnsweredStateChange(isAnswered: boolean) {
        if (isAnswered)
            this.autoNavigationAllowed(true);
    }

    private onTopicChangeRequested(topicId: string) {
        if (this.topicId() == topicId)
            this.autoNavigationAllowed(true);
    }
}